import {
    InsightGetSymbolFundamentalsRequestModeEnum,
    InsightOrderingDirectionEnum,
    InsightPaging,
    InsightSymbolSocialsResponse,
} from '@orbis-systems/orbis-api-clients/insight';
import { chunk, isEmpty, multiply, uniqBy } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import {
    availableCoins,
    BondQuantityType,
    CorporateActionsType,
    MostActiveOrderFilter,
    PriceType,
} from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    Apis,
    BondQuote,
    CorporateAction,
    CryptoCoin,
    DefinedAsset,
    EtfBreakdownEntry,
    MiddlewareConfig,
    MostActive,
    MostActiveWithExtraInfo,
    OptionChain,
    OptionChainDates,
    Quote,
    QuoteFundamentals,
    Shortability,
    TiprankOverviewWithExtraInfo,
} from '../types';
import { formatOptionChain, formatQuote, formatQuoteFundamentals, getUuid, parseCryptoSymbol } from '../utils';
import type { TipranksApi } from './TipranksApi';

@injectable()
export class QuoteApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.TipranksApi) protected readonly tipranksApi: TipranksApi,
    ) {
        super(apis, config);
    }

    async getTopTenAssets(): Promise<Quote[]> {
        const response = await this.apis.orbis.research.topStocks('MA', { market: 'BIG3', order: 'H' });

        return (response.data as EtfBreakdownEntry[]).map((item) => item.quote);
    }

    getStablecoin(): Promise<CryptoCoin | null> {
        throw new Error('Not supported');
    }

    getAvailableAssets(): Promise<DefinedAsset[]> {
        return Promise.resolve<DefinedAsset[]>(Object.values(availableCoins));
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */

    getCoin(symbol: string): Promise<CryptoCoin | null> {
        const { base } = parseCryptoSymbol(symbol);
        return Promise.resolve(availableCoins[base] || null);
    }

    async searchQuotes(criteria: string, limit?: number): Promise<Quote[]> {
        let results: Quote[];
        if (this.apis.aesMidas) {
            results = (await this.apis.aesMidas.quotesReformat.getSearch({ criteria, limit, loadQuotes: true })).data;
        } else {
            results = (
                await this.apis.orbis.quotes.search({
                    criteria: criteria.toUpperCase(),
                    limit: limit || 10,
                    loadQuotes: true,
                })
            ).data;
        }
        return results.map(formatQuote);
    }

    async searchBondQuotes(criteria: string, limit?: number): Promise<Quote[]> {
        let results: Quote[];
        if (this.apis.aesMidas) {
            results = (await this.apis.aesMidas.quotesReformat.getBondsSearch({ criteria, limit, loadQuotes: true }))
                .data;
        } else {
            results = (
                await this.apis.orbis.quotes.bondsSearch({
                    criteria: criteria.toUpperCase(),
                    limit: limit || 10,
                    loadQuotes: true,
                })
            ).data;
        }

        return results.map(formatQuote).map((quote) => ({
            ...quote,
            lastPrice: multiply(quote.lastPrice || 0, 100),
        }));
    }

    async getQuoteSet(): Promise<Quote[]> {
        throw new Error('Not supported');
    }

    async getQuotes(symbols: string[], crypto?: boolean): Promise<Quote[]> {
        const maxQuotes = 50; // API limitations
        const responses = await Promise.all(
            chunk(symbols, maxQuotes).map(async (symbolList) => {
                const symbols = symbolList.join(',');
                if (crypto) {
                    return this.apis.orbis.quotes.crypto({ symbols });
                } else if (this.apis.aesMidas) {
                    return this.apis.aesMidas.quotesReformat.getEquity({
                        symbols,
                    });
                } else {
                    return this.apis.orbis.quotes.equity({ symbols });
                }
            }),
        );
        const quotes = responses.flatMap((response) => response.data);
        // Equity endpoint still returns crypto data for some reason, so we filter here to make sure no duplicates
        const filteredData = uniqBy(quotes, (item) => item.symbol);
        return filteredData.map(formatQuote).filter((quote) => !quote.notFound);
    }

    async getQuote(symbol: string, crypto?: boolean): Promise<Quote | null> {
        let quotes: Quote[] = [];
        if (crypto) {
            quotes = (await this.apis.orbis.quotes.crypto({ symbols: symbol })).data;
        } else if (this.apis.aesMidas) {
            quotes = (await this.apis.aesMidas.quotesReformat.getEquity({ symbols: symbol })).data;
        } else {
            quotes = (await this.apis.orbis.quotes.equity({ symbols: symbol })).data;
        }
        const quote = quotes.find((quote) => quote.symbol === symbol.toUpperCase());
        return quote ? formatQuote(quote) : null;
    }
    // Orbis API takes multiple ISIN separated by commas, but we are only sending one for now until we can have insights support same logic
    async getBondQuote(identifiers: string): Promise<BondQuote | null> {
        let quote;

        if (this.apis.aesMidas) {
            quote = (
                await this.apis.aesMidas.quotesReformat.getBonds({
                    identifiers,
                    bondPriceType: PriceType.Percentage,
                    bondQuantityType: BondQuantityType.Unit,
                })
            ).data[0];
        } else {
            quote = (
                await this.apis.orbis.quotes.bonds({
                    isin: identifiers,
                    // @ts-expect-error: // FIXME: These params below are not added in docuementation correctly
                    bondPriceType: PriceType.Percentage,
                    bondQuantityType: BondQuantityType.Unit,
                })
            ).data[0];
        }

        if (quote.notFound || isEmpty(quote)) {
            throw Error('Quote not found');
        }

        const insightFixedIncomeInfo = (await this.apis.insight.fixedIncome.getSingleFixedIncomeEntry(identifiers))
            .data;

        quote = { ...quote, ...insightFixedIncomeInfo };

        // Description is a better value for companyName. We call for companyName from Quote in various components so this makes it more consistent
        const formattedBondQuote = {
            companyName: quote.issuer || quote.companyName,
            lastPrice: quote.lastPrice || 0,
            ask: quote.ask || 0,
            bid: quote.bid || 0,
        };

        return { ...quote, ...formattedBondQuote };
    }

    async getOptionQuote(symbol: string): Promise<Quote | null> {
        let quote;

        if (this.apis.aesMidas) {
            quote = (await this.apis.aesMidas.quotesReformat.getOption({ symbols: symbol })).data[0];
        } else {
            quote = (await this.apis.orbis.quotes.option({ symbols: symbol, greeks: true })).data[0];
        }

        if (isEmpty(quote)) {
            throw Error('Quote not found');
        }

        return quote;
    }

    async getQuoteOptionChainDates(symbol: string): Promise<OptionChainDates> {
        let data;
        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getOptionChainDates(symbol)).data;
        } else {
            data = (await this.apis.orbis.quotes.optionChainDates(symbol)).data;
        }
        return data;
    }

    async getQuoteOptionChainQuotes(symbol: string, date: string): Promise<OptionChain> {
        let data;
        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getOptionChain(symbol, date)).data;
        } else {
            data = (await this.apis.orbis.quotes.optionChain(symbol, date)).data;
        }
        return formatOptionChain(data);
    }

    async getMostActiveStocks({
        order,
        paging,
        volume,
        tipranks,
    }: {
        order?: {
            direction: InsightOrderingDirectionEnum;
            field: MostActiveOrderFilter; // FIXME: will be changed to insights enum; waiting for backend to update docs
        };
        paging: InsightPaging;
        volume?: number;
        tipranks: boolean;
    }): Promise<MostActiveWithExtraInfo[]> {
        const mostActiveResponse = await this.apis.insight.stockScreener.getTopSymbols({
            order,
            paging,
            exchange: ['NYSE', 'NAS', 'ASE'],
            volume: {
                more_than: volume,
            },
            market_cap: {
                more_than: 0,
            },
        });

        const mostActive = mostActiveResponse.data.details;
        const symbols = mostActive.map((item) => item.symbol);
        //FIXME: adding Daniils quote until insights will fix their quote data
        const quotes = await this.getQuotes(symbols);

        let tipranksData: TiprankOverviewWithExtraInfo[] = [];
        if (tipranks) {
            try {
                tipranksData = await this.tipranksApi.getTipranksAssetsOverview(symbols);
            } catch {
                tipranksData = [];
            }
        }

        return mostActive.map((mostActiveItem: MostActive) => {
            const currentQuote = quotes.find((quote) => quote.symbol === mostActiveItem.symbol);
            return {
                ...mostActiveItem,
                last_price: currentQuote?.lastPrice || mostActiveItem.last_price,
                price_change: currentQuote?.change || mostActiveItem.price_change,
                price_change_percent: currentQuote?.changePercent || mostActiveItem.price_change_percent,
                tipranks: tipranksData.find((item) => item.ticker === mostActiveItem.symbol),
            };
        });
    }

    async getQuoteShortability(symbol: string): Promise<Shortability> {
        const response = await this.apis.orbis.quotes.shortability(symbol);
        return response.data;
    }

    async getQuoteSocials(symbol: string) {
        const response = await this.apis.insight.logos.logosSocialSymbolLogos({ symbol });

        return response.data as InsightSymbolSocialsResponse;
    }

    async getCorporateActions(
        symbols: string[],
        dateFrom: string,
        dateTo: string,
        types?: CorporateActionsType,
    ): Promise<CorporateAction[]> {
        const actions = (
            await this.apis.orbis.research.corporateActions.search({
                symbol: symbols.join(','),
                dateFrom,
                dateTo,
                type: types,
            })
        ).data as CorporateAction[];

        return actions;
    }

    async getQuoteFundamentals(
        symbol: string,
        mode: InsightGetSymbolFundamentalsRequestModeEnum,
    ): Promise<QuoteFundamentals> {
        const response = await this.apis.insight.fundamentals.getFundamentalsBySymbolData({ symbol, mode });
        return formatQuoteFundamentals(response.data);
    }

    async getQuoteProfile(symbol: string) {
        const response = await this.apis.insight.companyProfile.getCombinedProfile({ symbol });
        return response.data;
    }

    async groupSymbolsByFundType(symbols: string[]) {
        const response = await this.apis.insight.companyProfile.getSymbolsPerSymbolType({ symbols });
        return response.data;
    }

    async getQuotesSnapshot(symbols: string[], userId?: number): Promise<Quote[]> {
        const response = await this.apis.orbis.orders.v2.equities.previewSnapshot({
            symbols: symbols.join(', '),
            // referenceId can be used if you want to track who is sending the requests
            referenceId: userId || getUuid(),
        });
        return response.data;
    }
}
